// Hash-based router for screen navigation
import { FLOW_CONFIG } from '../config/flow.js';
import type { PreloaderService } from '../utils/preloader';

export class Router {
  private screenController: any;
  private currentScreen: any;
  private history: string[];
  private preloaderService: PreloaderService | null;

  constructor(screenController) {
    this.screenController = screenController;
    this.currentScreen = null;
    this.history = [];
    this.preloaderService = null;
  }

  setPreloaderService(service: PreloaderService) {
    this.preloaderService = service;
  }

  init() {
    // Listen to hash changes
    window.addEventListener('hashchange', () => this.handleRoute());
    window.addEventListener('popstate', () => this.handleRoute());

    // Handle initial route
    this.handleRoute();
  }

  handleRoute() {
    const hash = window.location.hash || `#${FLOW_CONFIG.initialScreen}`;
    const screen = FLOW_CONFIG.getScreenByHash(hash);

    if (screen) {
      this.navigateToScreen(screen);
    } else {
      // Default to initial screen if hash not found
      this.navigateTo(FLOW_CONFIG.initialScreen);
    }
  }

  navigateToScreen(screen) {
    if (this.currentScreen?.id === screen.id) {
      return; // Already on this screen
    }

    // Add to history
    if (this.currentScreen) {
      this.history.push(this.currentScreen.id);
    }

    // Update current screen
    this.currentScreen = screen;

    // Trigger steps-before preloads
    if (this.preloaderService) {
      this.preloaderService.checkStepsBeforePreloads(screen.id);
    }

    // Swap screen in UI
    this.screenController.swapScreen(screen);

    // Update progress
    this.updateProgress();

    // Track analytics
    if (screen.analytics) {
      this.trackScreen(screen.analytics);
    }
  }

  navigateTo(screenId) {
    const screen = FLOW_CONFIG.getScreenById(screenId);
    if (screen) {
      window.location.hash = screen.hash;
    }
  }

  navigateNext() {
    if (this.currentScreen) {
      const nextScreen = FLOW_CONFIG.getNextScreen(this.currentScreen.id);
      if (nextScreen) {
        this.navigateTo(nextScreen.id);
      }
    }
  }

  navigateBack() {
    if (this.currentScreen?.back) {
      this.navigateTo(this.currentScreen.back);
    } else if (this.history.length > 0) {
      const previousId = this.history.pop();
      this.navigateTo(previousId);
    }
  }

  updateProgress() {
    if (!this.currentScreen) return;

    const total = FLOW_CONFIG.getTotalScreens();
    const current = FLOW_CONFIG.getScreenIndex(this.currentScreen.id);
    const percentage = (current / total) * 100;

    // Update progress bar
    const progressBar = document.getElementById('progress-bar-fill');
    if (progressBar) {
      progressBar.style.width = `${percentage}%`;
    }

    // Update screen counter
    const currentSpan = document.getElementById('current-screen');
    const totalSpan = document.getElementById('total-screens');
    if (currentSpan) currentSpan.textContent = current;
    if (totalSpan) totalSpan.textContent = total;
  }

  trackScreen(analytics) {
    // Dispatch custom event for analytics tracking
    document.dispatchEvent(new CustomEvent('analytics:screen', {
      detail: analytics
    }));
  }

  getCurrentScreen() {
    return this.currentScreen;
  }
}